import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  TrendingUp,
  Users,
  DollarSign,
  Award,
  Target,
  Eye,
} from 'lucide-react';
import RiskScoreBadge from './RiskScoreBadge';

interface StrategyCardProps {
  id: string;
  name: string;
  manager: string;
  roi: number;
  winRate?: number;
  totalProfit: number;
  totalInvested: number;
  investors: number;
  riskLevel: 'low' | 'medium' | 'high';
  riskScore?: number;
  status: 'active' | 'inactive' | 'pending';
  minCopyAmount?: number;
  description?: string;
  actionLabel?: string;
  onView?: () => void;
  onInvest?: () => void;
}

const StrategyCard = ({
  name,
  manager,
  roi,
  winRate,
  totalProfit,
  totalInvested,
  investors,
  riskLevel,
  riskScore,
  status,
  minCopyAmount,
  description,
  actionLabel = 'Copy Strategy',
  onView,
  onInvest,
}: StrategyCardProps) => {
  const getRiskColor = () => {
    switch (riskLevel) {
      case 'low':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300';
      case 'medium':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300';
      case 'high':
        return 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'active':
        return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30';
      case 'inactive':
        return 'bg-gray-500/15 text-gray-700 dark:text-gray-400 border-gray-500/30';
      case 'pending':
        return 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30';
      default:
        return 'bg-gray-500/15 text-gray-700 dark:text-gray-400 border-gray-500/30';
    }
  };

  const getInitials = (nameStr: string) => {
    return nameStr
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card className="group relative flex flex-col justify-between overflow-hidden border bg-card hover:border-primary/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <div>
        {/* Top Accent Gradient Bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 group-hover:opacity-100 opacity-80 transition-opacity" />

        <CardHeader className="p-4 sm:p-5 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {/* Manager Avatar Icon */}
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 font-bold text-white shadow-sm text-sm">
                {getInitials(manager)}
              </div>
              <div className="min-w-0 flex-1">
                <CardTitle className="text-base sm:text-lg font-bold truncate leading-tight group-hover:text-primary transition-colors">
                  {name}
                </CardTitle>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  by{' '}
                  <span className="font-medium text-foreground">{manager}</span>
                </p>
              </div>
            </div>

            <Badge
              variant="outline"
              className={`${getStatusColor()} shrink-0 text-[11px] font-semibold px-2 py-0.5`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="p-4 sm:p-5 pt-0 space-y-4">
          {/* Main Return Highlight Box */}
          <div className="flex items-center justify-between rounded-xl bg-muted/40 p-3 border">
            <div>
              <span className="text-xs font-medium text-muted-foreground block">
                Total ROI
              </span>
              <span className="text-xl sm:text-2xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight flex items-center gap-1">
                <TrendingUp className="h-5 w-5 stroke-[2.5]" />+{roi.toFixed(2)}
                %
              </span>
            </div>
            {winRate !== undefined && (
              <div className="text-right border-l pl-3">
                <span className="text-xs font-medium text-muted-foreground block">
                  Win Rate
                </span>
                <span className="text-sm sm:text-base font-bold text-blue-600 dark:text-blue-400 flex items-center justify-end gap-1">
                  <Target className="h-3.5 w-3.5" />
                  {winRate.toFixed(1)}%
                </span>
              </div>
            )}
          </div>

          {description && (
            <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
              {description}
            </p>
          )}

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs">
            <div className="flex items-center gap-2 p-2 rounded-lg bg-background border">
              <DollarSign className="h-4 w-4 text-emerald-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] text-muted-foreground truncate">
                  Total Profit
                </p>
                <p className="font-semibold text-foreground truncate">
                  ${totalProfit.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 p-2 rounded-lg bg-background border">
              <Award className="h-4 w-4 text-indigo-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] text-muted-foreground truncate">
                  AUM
                </p>
                <p className="font-semibold text-foreground truncate">
                  ${totalInvested.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 p-2 rounded-lg bg-background border">
              <Users className="h-4 w-4 text-amber-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] text-muted-foreground truncate">
                  Copiers
                </p>
                <p className="font-semibold text-foreground truncate">
                  {investors} Traders
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 p-2 rounded-lg bg-background border">
              <DollarSign className="h-4 w-4 text-purple-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] text-muted-foreground truncate">
                  Min. Copy
                </p>
                <p className="font-semibold text-foreground truncate">
                  ${minCopyAmount ? minCopyAmount.toLocaleString() : '50'}
                </p>
              </div>
            </div>
          </div>

          {/* Risk Level & Badges */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge
                variant="outline"
                className={`${getRiskColor()} text-[10px] font-semibold`}
              >
                Risk: {riskLevel.toUpperCase()}
              </Badge>
              {typeof riskScore === 'number' && (
                <RiskScoreBadge score={riskScore} />
              )}
            </div>
          </div>
        </CardContent>
      </div>

      {/* Responsive Action Buttons Footer */}
      <div className="p-4 sm:p-5 pt-0 border-t bg-muted/10 mt-2">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-3">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 whitespace-nowrap text-xs font-semibold"
            onClick={onView}
          >
            <Eye className="h-3.5 w-3.5 mr-1.5" />
            View Details
          </Button>
          {status === 'active' && (
            <Button
              size="sm"
              className="flex-1 whitespace-nowrap text-xs font-semibold bg-primary hover:bg-primary/90"
              onClick={onInvest}
            >
              {actionLabel}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default StrategyCard;
